import { useState, useEffect } from 'react';
import { offlineManager } from '../services/offlineManager';

/**
 * Hook to manage offline functionality
 */
export const useOfflineManager = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [cacheStats, setCacheStats] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Listen for online/offline status changes
    const unsubscribe = offlineManager.addListener((status, online) => {
      setIsOnline(online);
    });

    // Initial status
    setIsOnline(offlineManager.isOnline);

    return unsubscribe;
  }, []);

  /**
   * Get cache statistics
   */
  const getCacheStats = async () => {
    try {
      setLoading(true);
      const stats = await offlineManager.getCacheStatistics();
      setCacheStats(stats);
      return stats;
    } catch (error) {
      // console.error('Error getting cache stats:', error);
      return null;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Clear all cached data
   */
  const clearCache = async () => {
    try {
      setLoading(true);
      const success = await offlineManager.clearAllCache();
      if (success) {
        setCacheStats(null);
      }
      return success;
    } catch (error) {
      // console.error('Error clearing cache:', error);
      return false;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Check if content is available offline
   */
  const isContentAvailable = async (contentId, contentType) => {
    return await offlineManager.isContentAvailableOffline(contentId, contentType);
  };

  /**
   * Preload content for offline use
   */
  const preloadContent = async (items, contentType, extractContentInfo) => {
    try {
      setLoading(true);
      return await offlineManager.preloadContent(items, contentType, extractContentInfo);
    } catch (error) {
      // console.error('Error preloading content:', error);
      return false;
    } finally {
      setLoading(false);
    }
  };

  return {
    isOnline,
    cacheStats,
    loading,
    getCacheStats,
    clearCache,
    isContentAvailable,
    preloadContent,
    offlineManager
  };
};

export default useOfflineManager;